import { useEffect, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import {
  Package, Plus, Search, Edit2, Trash2, ChevronDown, ChevronUp,
  Tag, DollarSign,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getSupabaseClient } from "../providers/supabase/supabase";

type Product = {
  id: number;
  sku: string;
  name_en: string;
  name_zh: string | null;
  category: string | null;
  status: string;
  base_price_usd: number | null;
  base_price_eur: number | null;
  pieces_per_carton: number | null;
  gross_weight_kg: number | null;
  material: string | null;
  hs_code: string | null;
  description: string | null;
  created_at: string;
};

type Variant = {
  id: number;
  product_id: number;
  color: string | null;
  size_range: string[] | null;
  sku_variant: string | null;
  additional_cost: number;
};

type PriceRule = {
  id: number;
  product_id: number;
  vip_level: string;
  incoterms: string | null;
  discount_percent: number;
  override_price: number | null;
  valid_from: string | null;
  valid_to: string | null;
};

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  discontinued: "bg-red-100 text-red-700",
  development: "bg-yellow-100 text-yellow-700",
};

const VIP_LEVELS = ["standard", "silver", "gold", "platinum", "diamond"];
const VIP_LABELS: Record<string, string> = {
  standard: "注册客户 Registered",
  silver:   "🥈 银牌 Silver",
  gold:     "🥇 金牌 Gold",
  platinum: "💎 铂金 Platinum",
  diamond:  "👑 钻石 Diamond",
};

const emptyForm = () => ({
  sku: "", name_en: "", name_zh: "", category: "", status: "active",
  base_price_usd: "", base_price_eur: "",
  pieces_per_carton: "", gross_weight_kg: "",
  material: "", hs_code: "", description: "",
});

// ─── Variants sub-component ────────────────────────────────────────────────────
const VariantsPanel = ({ productId }: { productId: number }) => {
  const notify = useNotify();
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState({ color: "", size_range: "", sku_variant: "", additional_cost: "" });
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    const { data } = await getSupabaseClient().from("product_variants").select("*").eq("product_id", productId).order("id");
    setVariants(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [productId]);

  const handleAdd = async () => {
    setAdding(true);
    const { error } = await getSupabaseClient().from("product_variants").insert({
      product_id: productId,
      color: addForm.color || null,
      size_range: addForm.size_range ? addForm.size_range.split(",").map(s => s.trim()) : null,
      sku_variant: addForm.sku_variant || null,
      additional_cost: parseFloat(addForm.additional_cost) || 0,
    });
    if (error) notify(error.message, { type: "error" });
    else { notify("变体已添加", { type: "success" }); setAddForm({ color: "", size_range: "", sku_variant: "", additional_cost: "" }); setShowAdd(false); load(); }
    setAdding(false);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("删除此变体？")) return;
    await getSupabaseClient().from("product_variants").delete().eq("id", id);
    load();
  };

  if (loading) return <div className="text-xs text-muted-foreground">加载中...</div>;

  return (
    <div className="space-y-2">
      {variants.length === 0 && !showAdd && (
        <p className="text-xs text-muted-foreground">暂无颜色/尺码变体</p>
      )}
      {variants.map(v => (
        <div key={v.id} className="flex items-center gap-3 text-xs px-3 py-2 bg-muted/40 rounded-lg">
          {v.color && <span className="font-medium">{v.color}</span>}
          {v.size_range?.length && <span className="text-muted-foreground">{v.size_range.join(", ")}</span>}
          {v.sku_variant && <span className="font-mono text-muted-foreground">{v.sku_variant}</span>}
          {v.additional_cost > 0 && <span className="text-orange-600">+${v.additional_cost}</span>}
          <button onClick={() => handleDelete(v.id)} className="ml-auto text-muted-foreground hover:text-destructive">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}

      {showAdd ? (
        <div className="border rounded-lg p-3 space-y-2 bg-background">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">颜色/款式</Label>
              <Input className="h-7 text-xs mt-0.5" placeholder="White / Navy" value={addForm.color} onChange={e => setAddForm(f => ({...f, color: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">尺码（逗号分隔）</Label>
              <Input className="h-7 text-xs mt-0.5" placeholder="S,M,L,XL,2XL" value={addForm.size_range} onChange={e => setAddForm(f => ({...f, size_range: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">变体 SKU</Label>
              <Input className="h-7 text-xs mt-0.5 font-mono" placeholder="SP-POLO-WHT" value={addForm.sku_variant} onChange={e => setAddForm(f => ({...f, sku_variant: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">附加成本 (USD)</Label>
              <Input className="h-7 text-xs mt-0.5" type="number" step="0.01" value={addForm.additional_cost} onChange={e => setAddForm(f => ({...f, additional_cost: e.target.value}))} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs" onClick={handleAdd} disabled={adding}>{adding ? "添加中..." : "确认添加"}</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowAdd(false)}>取消</Button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 text-xs text-primary hover:underline">
          <Plus className="w-3 h-3" /> 添加变体
        </button>
      )}
    </div>
  );
};

// ─── VIP Pricing sub-component ─────────────────────────────────────────────────
const VIPPricingPanel = ({ productId, basePriceUsd }: { productId: number; basePriceUsd: number | null }) => {
  const notify = useNotify();
  const [rules, setRules] = useState<PriceRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [addLevel, setAddLevel] = useState("");
  const [addDiscount, setAddDiscount] = useState("");
  const [addOverride, setAddOverride] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await getSupabaseClient().from("price_rules").select("*").eq("product_id", productId).order("vip_level");
    setRules(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [productId]);

  const handleAdd = async () => {
    if (!addLevel) return;
    setAdding(true);
    const { error } = await getSupabaseClient().from("price_rules").insert({
      product_id: productId,
      vip_level: addLevel,
      discount_percent: parseFloat(addDiscount) || 0,
      override_price: addOverride ? parseFloat(addOverride) : null,
    });
    if (error) notify(error.message, { type: "error" });
    else { notify("价格规则已添加", { type: "success" }); setAddLevel(""); setAddDiscount(""); setAddOverride(""); load(); }
    setAdding(false);
  };

  const handleDelete = async (id: number) => {
    await getSupabaseClient().from("price_rules").delete().eq("id", id);
    load();
  };

  if (loading) return <div className="text-xs text-muted-foreground">加载中...</div>;

  return (
    <div className="space-y-2">
      {rules.length === 0 && <p className="text-xs text-muted-foreground">暂无 VIP 折扣规则，所有客户使用基准价</p>}
      {rules.map(r => {
        const effectivePrice = r.override_price ?? (basePriceUsd ? basePriceUsd * (1 - r.discount_percent / 100) : null);
        return (
          <div key={r.id} className="flex items-center gap-3 text-xs px-3 py-2 bg-muted/40 rounded-lg">
            <span className="font-medium w-20">{VIP_LABELS[r.vip_level] ?? r.vip_level}</span>
            <span className="text-muted-foreground">{r.discount_percent > 0 ? `-${r.discount_percent}%` : ""}</span>
            {r.override_price && <span className="text-muted-foreground">固定 ${r.override_price}</span>}
            {effectivePrice && <span className="font-semibold text-primary">${effectivePrice.toFixed(2)}</span>}
            <button onClick={() => handleDelete(r.id)} className="ml-auto text-muted-foreground hover:text-destructive">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        );
      })}
      <div className="grid grid-cols-3 gap-2 pt-1">
        <Select value={addLevel} onValueChange={setAddLevel}>
          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="VIP等级" /></SelectTrigger>
          <SelectContent>
            {VIP_LEVELS.filter(l => !rules.find(r => r.vip_level === l)).map(l => (
              <SelectItem key={l} value={l}>{VIP_LABELS[l]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input className="h-7 text-xs" type="number" step="1" max={50} placeholder="折扣 % (如 10)" value={addDiscount} onChange={e => setAddDiscount(e.target.value)} />
        <div className="flex gap-1">
          <Input className="h-7 text-xs flex-1" type="number" step="0.01" placeholder="固定价 (可选)" value={addOverride} onChange={e => setAddOverride(e.target.value)} />
          <Button size="sm" className="h-7 px-2 shrink-0" onClick={handleAdd} disabled={!addLevel || adding}>
            {adding ? "..." : <Plus className="w-3 h-3" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main ProductsPage ──────────────────────────────────────────────────────────
const ProductsPage = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedTab, setExpandedTab] = useState<"variants" | "pricing">("variants");

  const load = () => {
    getSupabaseClient().from("products").select("*").order("created_at", { ascending: false })
      .then(({ data }) => { setProducts(data ?? []); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      sku: p.sku, name_en: p.name_en, name_zh: p.name_zh ?? "",
      category: p.category ?? "", status: p.status,
      base_price_usd: p.base_price_usd?.toString() ?? "",
      base_price_eur: p.base_price_eur?.toString() ?? "",
      pieces_per_carton: p.pieces_per_carton?.toString() ?? "",
      gross_weight_kg: p.gross_weight_kg?.toString() ?? "",
      material: p.material ?? "", hs_code: p.hs_code ?? "",
      description: p.description ?? "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.sku.trim() || !form.name_en.trim()) {
      notify("SKU 和产品名称为必填项", { type: "error" }); return;
    }
    setSaving(true);
    const supabase = getSupabaseClient();
    const payload = {
      sku: form.sku.trim(), name_en: form.name_en.trim(),
      name_zh: form.name_zh || null, category: form.category || null,
      status: form.status,
      base_price_usd: form.base_price_usd ? parseFloat(form.base_price_usd) : null,
      base_price_eur: form.base_price_eur ? parseFloat(form.base_price_eur) : null,
      pieces_per_carton: form.pieces_per_carton ? parseInt(form.pieces_per_carton) : null,
      gross_weight_kg: form.gross_weight_kg ? parseFloat(form.gross_weight_kg) : null,
      material: form.material || null, hs_code: form.hs_code || null,
      description: form.description || null, updated_at: new Date().toISOString(),
    };
    if (editing) {
      const { error } = await supabase.from("products").update(payload).eq("id", editing.id);
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("产品已更新", { type: "info" });
    } else {
      const { error } = await supabase.from("products").insert({ ...payload, created_by: identity?.id ?? null });
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("产品已创建", { type: "info" });
    }
    setSaving(false); setShowForm(false); load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("删除此产品？")) return;
    await getSupabaseClient().from("products").delete().eq("id", id);
    notify("已删除", { type: "info" }); load();
  };

  const filtered = products.filter(p => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.sku.toLowerCase().includes(q) || p.name_en.toLowerCase().includes(q) || (p.name_zh ?? "").includes(q);
    }
    return true;
  });

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-6 h-6" /> 产品目录</h1>
        <Button size="sm" onClick={openCreate} className="gap-1"><Plus className="w-3.5 h-3.5" /> 添加产品</Button>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="SKU、产品名称..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          {["all", "active", "development", "discontinued"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {s === "all" ? "全部" : s === "active" ? "在售" : s === "development" ? "开发中" : "停产"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>暂无产品。<button onClick={openCreate} className="text-primary underline">添加第一个产品</button></p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5 w-6"></th>
                <th className="text-left px-4 py-2.5">SKU / 名称</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">品类</th>
                <th className="text-right px-4 py-2.5">USD</th>
                <th className="text-right px-4 py-2.5 hidden lg:table-cell">EUR</th>
                <th className="text-center px-4 py-2.5">状态</th>
                <th className="px-4 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <>
                  <tr key={p.id} className="border-t hover:bg-muted/20 transition-colors">
                    <td className="px-2 py-3 text-center">
                      <button onClick={() => { setExpandedId(expandedId === p.id ? null : p.id); setExpandedTab("variants"); }}
                        className="text-muted-foreground hover:text-foreground">
                        {expandedId === p.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-muted-foreground">{p.sku}</div>
                      <div className="font-medium">{p.name_en}</div>
                      {p.name_zh && <div className="text-xs text-muted-foreground">{p.name_zh}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{p.category ?? "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {p.base_price_usd != null ? `$${p.base_price_usd.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums hidden lg:table-cell">
                      {p.base_price_eur != null ? `€${p.base_price_eur.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[p.status] ?? ""}`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(p)} className="p-1 text-muted-foreground hover:text-foreground"><Edit2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(p.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === p.id && (
                    <tr key={`exp-${p.id}`} className="bg-muted/10 border-t">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="flex gap-4 mb-3 border-b pb-2">
                          {([["variants", <Tag className="w-3.5 h-3.5" />, "颜色/变体"], ["pricing", <DollarSign className="w-3.5 h-3.5" />, "VIP 定价"]] as const).map(([tab, icon, label]) => (
                            <button key={tab} onClick={() => setExpandedTab(tab as "variants" | "pricing")}
                              className={`flex items-center gap-1 text-xs font-medium pb-1.5 border-b-2 transition-colors ${expandedTab === tab ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                              {icon}{label}
                            </button>
                          ))}
                        </div>
                        {expandedTab === "variants"
                          ? <VariantsPanel productId={p.id} />
                          : <VIPPricingPanel productId={p.id} basePriceUsd={p.base_price_usd} />
                        }
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "编辑产品" : "新建产品"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>SKU *</Label>
              <Input className="h-8 text-sm font-mono" placeholder="SP-POLO-WHT-M" value={form.sku} onChange={e => set("sku", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>状态</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">在售</SelectItem>
                  <SelectItem value="development">开发中</SelectItem>
                  <SelectItem value="discontinued">停产</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>品名（英文）*</Label>
              <Input className="h-8 text-sm" value={form.name_en} onChange={e => set("name_en", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>品名（中文）</Label>
              <Input className="h-8 text-sm" value={form.name_zh} onChange={e => set("name_zh", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>品类</Label>
              <Input className="h-8 text-sm" placeholder="上装 / 针织" value={form.category} onChange={e => set("category", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>HS 编码</Label>
              <Input className="h-8 text-sm font-mono" placeholder="6109.10" value={form.hs_code} onChange={e => set("hs_code", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>基准价 (USD)</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" value={form.base_price_usd} onChange={e => set("base_price_usd", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>基准价 (EUR)</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" value={form.base_price_eur} onChange={e => set("base_price_eur", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>件/箱</Label>
              <Input className="h-8 text-sm" type="number" value={form.pieces_per_carton} onChange={e => set("pieces_per_carton", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>毛重 (kg/箱)</Label>
              <Input className="h-8 text-sm" type="number" step="0.001" value={form.gross_weight_kg} onChange={e => set("gross_weight_kg", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>材质</Label>
              <Input className="h-8 text-sm" placeholder="100% Cotton" value={form.material} onChange={e => set("material", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>产品描述</Label>
              <Textarea className="text-sm resize-none" rows={2} value={form.description} onChange={e => set("description", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>取消</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "保存中..." : (editing ? "更新" : "创建")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
